/**
 * Le détail d'une pièce, et **toute son histoire de révisions**.
 *
 * La question à laquelle cet écran répond n'est pas « qu'est-ce que ce
 * fichier ? » mais « **sur quelle version ce métré a-t-il été calculé ?** ».
 * Une révision ne remplace pas la précédente, elle s'empile ; six mois plus
 * tard, c'est la seule chose qui rend un chiffrage vérifiable.
 *
 * D'où deux choix qui ne sont pas cosmétiques : chaque révision se télécharge
 * **individuellement** — la courante ne sert à rien pour justifier un métré
 * remis en juin — et l'empreinte s'affiche **entière**, parce que son seul
 * usage est de se comparer à celle d'un fichier qu'un client renvoie.
 */
import { api } from "../../api/client";
import { useModale } from "../../composants/clavier";
import { Chargement, Echec } from "../../composants/etats";
import { Etat } from "../../composants/etiquettes";
import { poids, quand } from "../../composants/formats";
import { useDonnees } from "../../composants/donnees";

interface Revision {
  revision_id: string;
  etag: string;
  sha256: string;
  octets: number;
  mime: string;
  importe_le: string;
}

interface Detail {
  document_id: string;
  nom: string;
  categorie: string;
  dossier: string;
  etat: string;
  drive_id: string;
  item_id: string;
  creee_le: string;
  modifiee_le: string;
  revisions: Revision[];
}

export function DetailDeLaPiece({
  documentId,
  fermer,
}: {
  documentId: string;
  fermer: () => void;
}) {
  const detail = useDonnees<{ donnees: Detail }>(
    () => api.lire(`/api/v1/pieces/${documentId}`),
    [documentId],
  );

  const refModale = useModale(true, fermer);

  const vue = detail.donnees?.donnees;

  return (
    <div ref={refModale} className="voile" role="dialog" aria-modal="true"
         aria-label="Détail de la pièce">
      <div className="modale modale--detail">
        <div className="modale__tete">
          <div className="pile pile--serree" style={{ flex: 1, minWidth: 0 }}>
            <h2 className="tronque">{vue?.nom ?? "Pièce"}</h2>
            {vue && (
              <span className="discret tronque">{vue.dossier || "—"}</span>
            )}
          </div>
          <button type="button" className="bouton" onClick={fermer}>
            Fermer
          </button>
        </div>

        <div className="modale__corps" style={{ padding: "var(--mo-space-5)" }}>
          {detail.charge && !detail.donnees ? (
            <Chargement lignes={4} />
          ) : detail.erreur ? (
            <Echec erreur={detail.erreur} reessayer={detail.recharger} />
          ) : vue ? (
            <div className="pile">
              <dl className="fiche">
                <div><dt>État</dt>
                  <dd><Etat genre="piece" valeur={vue.etat} /></dd></div>
                <div><dt>Catégorie</dt><dd>{vue.categorie}</dd></div>
                <div><dt>Rattachée le</dt>
                  <dd className="mono">{quand(vue.creee_le)}</dd></div>
                <div><dt>Dernier changement</dt>
                  <dd className="mono">{quand(vue.modifiee_le)}</dd></div>
                {/* Ce que le support demande quand une pièce se comporte mal.
                    Sans jeton, ces identifiants ne désignent rien. */}
                <div><dt>Élément OneDrive</dt>
                  <dd className="mono tronque">{vue.item_id}</dd></div>
              </dl>

              <section>
                <h3 style={{ marginBottom: "var(--mo-space-2)" }}>
                  Révisions importées
                </h3>

                {vue.revisions.length === 0 ? (
                  <div className="note">
                    <span>
                      Aucune révision : la pièce est rattachée mais n'a pas
                      encore été descendue. Il n'y a donc rien à télécharger.
                    </span>
                  </div>
                ) : (
                  <div className="liste liste--revisions">
                    <div className="liste__entete">
                      <span>Importée le</span>
                      <span>Taille</span>
                      <span>Empreinte (sha256)</span>
                      <span />
                    </div>
                    {vue.revisions.map((revision, rang) => (
                      <div key={revision.revision_id}
                           className="liste__ligne liste__ligne--fixe">
                        <span className="mono">
                          {quand(revision.importe_le)}
                          {rang === 0 && (
                            <span className="etiquette etiquette--courante">
                              courante
                            </span>
                          )}
                        </span>
                        <span className="discret mono">
                          {poids(revision.octets)}
                        </span>
                        {/* Entière, et sélectionnable : on la colle dans un
                            courriel pour prouver qu'il s'agit du même
                            fichier. Tronquée, elle ne prouve plus rien. */}
                        <span className="mono empreinte">{revision.sha256}</span>
                        <a
                          className="bouton bouton--discret"
                          href={`/api/v1/pieces/${documentId}/contenu?revision=${revision.revision_id}`}
                        >
                          Télécharger
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
